import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import translate from "@insite/client-framework/Translate";
import Tooltip from "@insite/mobius/Tooltip/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    productNumber: string;
    customerProductNumber?: string;
    manufacturerItem?: string;
    serialNumbers?: string;
    showCustomerName?: boolean;
    showLabel?: boolean;
    showManufacturerItem?: boolean;
    extendedStyles?: ProductPartNumbersStyles;
    showProductNumber?: boolean;
}

type Props = OwnProps;

export interface ProductPartNumbersStyles {
    container?: InjectableCss;
    GridViewContainer?: InjectableCss;
    condensed?: boolean;
    erpNumberWrapper?: InjectableCss;
    labelText?: TypographyPresentationProps;
    valueText?: TypographyPresentationProps;
    erpNumberLabelText?: TypographyPresentationProps;
    erpNumberValueText?: TypographyPresentationProps;
    customerNameWrapper?: InjectableCss;
    customerNameLabelText?: TypographyPresentationProps;
    customerNameValueText?: TypographyPresentationProps;
    manufacturerItemWrapper?: InjectableCss;
    manufacturerItemLabelText?: TypographyPresentationProps;
    manufacturerItemValueText?: TypographyPresentationProps;
}

export const productPartNumbersStyles: ProductPartNumbersStyles = {
    labelText: {
        size: "12px",
        lineHeight: "18px",
        color: "text.main",
        css: css`
            flex: 0 0 auto;
            margin-right: 4px;
        `,
    },
    valueText: {
        weight: "bold",
        size: "12px",
        lineHeight: "18px",
        color: "text.main",
        ellipsis: true,
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
        `,
    },
};

const ProductPartNumbers: FC<Props> = ({
    productNumber,
    customerProductNumber,
    manufacturerItem,
    serialNumbers,
    showCustomerName = true,
    showManufacturerItem = true,
    showLabel = true,
    extendedStyles,
    showProductNumber = true,
}) => {
    const [styles] = React.useState(() => mergeToNew(productPartNumbersStyles, extendedStyles));
    const displayManufacturerNumber = showManufacturerItem && manufacturerItem;
    const displayCustomerNumber = showCustomerName && customerProductNumber;
    const serialNumbersArray = serialNumbers && serialNumbers.split("~").filter(str => str !== "");

    return (
        <StyledWrapper {...styles.container} className="parts-num-container">
            {showProductNumber && productNumber?.length > 0 && (
                <StyledWrapper {...styles.erpNumberWrapper} className="item-cont">
                    {showLabel && (
                        <Typography {...mergeToNew(styles.erpNumberLabelText, styles.labelText)}>
                            {translate("Part #")}:
                        </Typography>
                    )}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...mergeToNew(styles.erpNumberLabelText, styles.labelText)}>
                                {translate("Part #")}
                            </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography
                        translate="no"
                        {...mergeToNew(styles.erpNumberLabelText, styles.valueText)}
                        data-test-selector="productNumber"
                    >
                        {productNumber}
                    </Typography>
                </StyledWrapper>
            )}
            {displayCustomerNumber && (
                <StyledWrapper {...styles.customerNameWrapper} className="item-cont">
                    {showLabel && (
                        <Typography {...mergeToNew(styles.customerNameLabelText, styles.labelText)}>
                            {translate("Customer Part #")}:
                        </Typography>
                    )}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...mergeToNew(styles.customerNameLabelText, styles.labelText)}>
                                {translate("Customer Part #")}
                            </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography
                        translate="no"
                        {...mergeToNew(styles.customerNameValueText, styles.valueText)}
                        data-test-selector="customerProductNumber"
                    >
                        {customerProductNumber}
                    </Typography>
                </StyledWrapper>
            )}
            {displayManufacturerNumber && (
                <StyledWrapper {...styles.manufacturerItemWrapper} className="item-cont">
                    {showLabel && (
                        <Typography {...mergeToNew(styles.manufacturerItemLabelText, styles.labelText)}>
                            {translate("MFG #")}:
                        </Typography>
                    )}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...mergeToNew(styles.manufacturerItemLabelText, styles.labelText)}>
                                {translate("MFG #")}
                            </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography
                        translate="no"
                        {...mergeToNew(styles.manufacturerItemValueText, styles.valueText)}
                        data-test-selector="manufacturerItem"
                    >
                        {manufacturerItem}
                    </Typography>
                </StyledWrapper>
            )}
            {serialNumbers && serialNumbersArray && (
                <StyledWrapper {...styles.erpNumberWrapper} className="item-cont">
                    {showLabel && <Typography {...styles.labelText}>{translate("Serial #")}: </Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.labelText}>{translate("Serial #")} </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography translate="no" {...styles.valueText} data-test-selector="serialNumberItem">
                        {serialNumbersArray.length > 1 ? (
                            <Tooltip
                                triggerComponent={<Typography>{serialNumbersArray[0]}...</Typography>}
                                text={serialNumbersArray.join(", ")}
                            />
                        ) : (
                            serialNumbersArray[0]
                        )}
                    </Typography>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default ProductPartNumbers;
